import * as THREE from 'three';
import type { Command } from '@/core/Command';
import type { FloraInstance, Vec3 } from '@/flora/FloraInstance';
import { clampMaturity } from '@/flora/FloraInstance';
import type { FloraWorld } from '@/flora/FloraWorld';
import type { SelectionManager } from './SelectionManager';
import type { Terrain } from '@/terrain/Terrain';
import { TerrainRaycaster } from '@/terrain/TerrainRaycaster';
import { TransformFloraCommand, type FloraChange } from './FloraCommands';

/** Return a copy of `instance` translated by `delta` (meters). */
export function translateInstance(instance: FloraInstance, delta: Vec3): FloraInstance {
  return {
    ...instance,
    position: {
      x: instance.position.x + delta.x,
      y: instance.position.y + delta.y,
      z: instance.position.z + delta.z,
    },
  };
}

/** Return a copy of `instance` moved to `position`. */
export function moveInstanceTo(instance: FloraInstance, position: Vec3): FloraInstance {
  return { ...instance, position: { ...position } };
}

/** Return a copy of `instance` rotated about the vertical (Y) axis by `deltaRadians`. */
export function rotateInstanceY(instance: FloraInstance, deltaRadians: number): FloraInstance {
  return {
    ...instance,
    rotation: { ...instance.rotation, y: instance.rotation.y + deltaRadians },
  };
}

/**
 * Return a copy of `instance` with a new maturity. Maturity drives non-uniform growth — it is never
 * a uniform object scale — so this only changes the maturity field (spec §3.6, §8.3).
 */
export function setInstanceMaturity(
  instance: FloraInstance,
  maturity: number,
  allowOvergrowth = false,
): FloraInstance {
  return { ...instance, maturity: clampMaturity(maturity, allowOvergrowth) };
}

/** Return a copy of `instance` with maturity adjusted by `delta`. */
export function adjustInstanceMaturity(
  instance: FloraInstance,
  delta: number,
  allowOvergrowth = false,
): FloraInstance {
  return setInstanceMaturity(instance, instance.maturity + delta, allowOvergrowth);
}

export type TransformMode = 'move' | 'rotate' | 'maturity';

/**
 * One class of placed thing, as the transform gesture sees it.
 *
 * The gesture is the same for a shrub, a bench and a rabbit — press a key, drag, click to keep or
 * Escape to put it back — and it was worth exactly one implementation. What differs between the
 * three is small, specific, and entirely below this line: what `y` means during a drag, whether
 * maturity is a thing you can adjust, and which command records the result. See `StaffageLayer` for
 * the first two.
 *
 * A subject holds its own snapshot of what it picked up between {@link begin} and {@link commit}, so
 * the tool never needs to know how many of the selected ids belonged to whom.
 */
export interface TransformSubject {
  /** False for a gesture this class has no meaning for — maturity, for anything that does not grow. */
  supports(mode: TransformMode): boolean;
  /**
   * Take a snapshot of whichever of `ids` this subject owns. Returns how many it took.
   *
   * The mode comes in here rather than at commit time so a subject can name its own history entry
   * after the gesture that produced it — "Move bench" rather than a generic "Transform".
   */
  begin(ids: readonly string[], mode: TransformMode): number;
  /** The ids picked up by the last {@link begin}, in selection order. */
  heldIds(): readonly string[];
  /** Where a held id was before the drag started, for measuring the pointer against. */
  anchor(id: string): Vec3 | null;
  /** Put every held item at its own pre-drag position plus this offset. Absolute, not incremental. */
  move(dx: number, dz: number): void;
  /** Turn every held item by an increment, from wherever it is now. */
  rotate(deltaRadians: number): void;
  /** Adjust maturity by an increment. Never called on a subject that does not support it. */
  maturity(delta: number): void;
  /** Put everything back exactly as it was picked up. */
  revert(): void;
  /** The net change as one history entry, or null when nothing actually moved. Drops the snapshot. */
  commit(): Command | null;
}

/**
 * Plants, as the transform gesture sees them.
 *
 * The one piece of real work is in {@link move}: a plant's `position.y` **is** its ground height, so
 * a plant that has been dragged has to be re-seated onto the terrain it landed on. Staffage does the
 * opposite and deliberately leaves `y` alone — see `StaffageLayer`.
 */
export class FloraTransformSubject implements TransformSubject {
  private originals = new Map<string, FloraInstance>();

  constructor(
    private readonly world: FloraWorld,
    private readonly terrain: () => Terrain | null,
    /** Shared props propagate to linked aliases once the drag is recorded (spec §4.5). */
    private readonly onCommitted?: (changes: readonly FloraChange[]) => void,
  ) {}

  supports(): boolean {
    return true;
  }

  begin(ids: readonly string[]): number {
    this.originals.clear();
    for (const id of ids) {
      const instance = this.world.get(id);
      if (instance) this.originals.set(id, clone(instance));
    }
    return this.originals.size;
  }

  heldIds(): readonly string[] {
    return [...this.originals.keys()];
  }

  anchor(id: string): Vec3 | null {
    const original = this.originals.get(id);
    return original ? { ...original.position } : null;
  }

  move(dx: number, dz: number): void {
    const terrain = this.terrain();
    for (const original of this.originals.values()) {
      const moved = translateInstance(original, { x: dx, y: 0, z: dz });
      if (terrain) moved.position.y = terrain.sampleHeight(moved.position.x, moved.position.z);
      this.world.update(moved);
    }
  }

  rotate(deltaRadians: number): void {
    for (const id of this.originals.keys()) {
      const current = this.world.get(id);
      if (current) this.world.update(rotateInstanceY(current, deltaRadians));
    }
  }

  maturity(delta: number): void {
    for (const id of this.originals.keys()) {
      const current = this.world.get(id);
      if (current) this.world.update(adjustInstanceMaturity(current, delta));
    }
  }

  revert(): void {
    for (const [id, original] of this.originals) {
      if (this.world.has(id)) this.world.update(original, { rebuild: true });
    }
  }

  commit(): Command | null {
    const changes: FloraChange[] = [];
    for (const [id, before] of this.originals) {
      const after = this.world.get(id);
      if (!after) continue;
      if (
        before.position.x !== after.position.x ||
        before.position.y !== after.position.y ||
        before.position.z !== after.position.z ||
        before.rotation.y !== after.rotation.y ||
        before.maturity !== after.maturity
      ) {
        changes.push({ before, after: clone(after) });
      }
    }
    this.originals.clear();
    if (changes.length === 0) return null;
    this.onCommitted?.(changes);
    // The plants were moved live during the drag, so this records what already happened rather than
    // doing it again — see `FloraEditor`, which pushes rather than executes what comes back.
    return new TransformFloraCommand(this.world, changes);
  }
}

function clone(instance: FloraInstance): FloraInstance {
  return { ...instance, position: { ...instance.position }, rotation: { ...instance.rotation } };
}

export interface TransformToolDeps {
  domElement: HTMLElement;
  getCamera: () => THREE.Camera;
  terrain: () => Terrain | null;
  selection: SelectionManager;
  /** The classes of thing this tool can pick up. Resolved per gesture, so a layer can arrive late. */
  subjects: () => readonly TransformSubject[];
  /**
   * Called once when a transform is committed, with one command per subject that changed anything.
   * The caller records them — this tool never touches the history itself.
   */
  onCommit?: (commands: readonly Command[], mode: TransformMode) => void;
}

/**
 * Interactive move/rotate/maturity tool for the current selection (spec §3.6). All edits are applied
 * to the canonical records (never by distorting the scene object): move updates position, rotate
 * updates Y rotation, and the "scale" gesture maps to maturity so plants grow biologically rather
 * than stretch. Commit with click, cancel (revert) with Esc.
 *
 * It drives {@link TransformSubject}s rather than one world, which is what lets a selection holding
 * a shrub, a bench and a rabbit be dragged as one thing and recorded as one history entry.
 */
export class TransformTool {
  private readonly raycaster = new TerrainRaycaster();
  private mode: TransformMode | null = null;
  private held: TransformSubject[] = [];
  private lastPointer: { x: number; y: number } | null = null;
  private moveAnchor: Vec3 | null = null;

  private readonly onPointerMove = (e: PointerEvent): void => this.handlePointerMove(e);
  private readonly onPointerDown = (e: PointerEvent): void => this.handlePointerDown(e);
  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleKeyDown(e);

  constructor(private readonly deps: TransformToolDeps) {}

  get activeMode(): TransformMode | null {
    return this.mode;
  }

  /** Enter a transform mode for the current selection. No-op if nothing usable is selected. */
  begin(mode: TransformMode): void {
    const ids = this.deps.selection.selected();
    if (ids.length === 0) return;

    // A subject that cannot do this gesture is not picked up at all, so pressing `M` over a mixed
    // selection matures the plants in it and leaves the benches exactly where they are.
    this.held = this.deps
      .subjects()
      .filter((subject) => subject.supports(mode) && subject.begin(ids, mode) > 0);
    if (this.held.length === 0) return;

    this.mode = mode;
    this.lastPointer = null;
    this.moveAnchor = null;
    this.attachListeners();
  }

  /** Commit the current transform (keep changes). */
  commit(): void {
    const mode = this.mode;
    if (mode) {
      const commands: Command[] = [];
      for (const subject of this.held) {
        const command = subject.commit();
        if (command) commands.push(command);
      }
      if (commands.length > 0) this.deps.onCommit?.(commands, mode);
    }
    this.mode = null;
    this.held = [];
    this.detachListeners();
  }

  /** Cancel the current transform, reverting everything to its pre-edit state. */
  cancel(): void {
    for (const subject of this.held) subject.revert();
    this.commit();
  }

  dispose(): void {
    this.detachListeners();
  }

  private attachListeners(): void {
    this.deps.domElement.addEventListener('pointermove', this.onPointerMove);
    this.deps.domElement.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('keydown', this.onKeyDown);
  }

  private detachListeners(): void {
    this.deps.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.deps.domElement.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  private pointerToNdc(e: PointerEvent): THREE.Vector2 {
    const rect = this.deps.domElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    return new THREE.Vector2(x, y);
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.mode) return;
    switch (this.mode) {
      case 'move':
        this.updateMove(e);
        break;
      case 'rotate':
        this.updateRotate(e);
        break;
      case 'maturity':
        this.updateMaturity(e);
        break;
    }
    this.lastPointer = { x: e.clientX, y: e.clientY };
  }

  /**
   * The point the drag is measured from.
   *
   * The primary selection when it is one of the things being dragged, and otherwise the first thing
   * that was: pressing `M` with a bench as primary picks up only the plants behind it, and a drag
   * anchored to something not in hand would translate the whole selection by a nonsense offset.
   */
  private anchorPosition(): Vec3 | null {
    const primary = this.deps.selection.primary;
    if (primary !== null) {
      for (const subject of this.held) {
        const anchor = subject.anchor(primary);
        if (anchor) return anchor;
      }
    }
    for (const subject of this.held) {
      const first = subject.heldIds()[0];
      const anchor = first === undefined ? null : subject.anchor(first);
      if (anchor) return anchor;
    }
    return null;
  }

  private updateMove(e: PointerEvent): void {
    const terrain = this.deps.terrain();
    if (!terrain) return;
    const hit = this.raycaster.intersect(this.pointerToNdc(e), this.deps.getCamera(), terrain.mesh);
    if (!hit) return;

    if (!this.moveAnchor) {
      const anchor = this.anchorPosition();
      if (!anchor) return;
      this.moveAnchor = anchor;
    }
    const dx = hit.x - this.moveAnchor.x;
    const dz = hit.z - this.moveAnchor.z;
    for (const subject of this.held) subject.move(dx, dz);
  }

  private updateRotate(e: PointerEvent): void {
    if (!this.lastPointer) return;
    const deltaRadians = (e.clientX - this.lastPointer.x) * 0.01;
    for (const subject of this.held) subject.rotate(deltaRadians);
  }

  private updateMaturity(e: PointerEvent): void {
    if (!this.lastPointer) return;
    const delta = (this.lastPointer.y - e.clientY) * 0.005;
    for (const subject of this.held) subject.maturity(delta);
  }

  private handlePointerDown(e: PointerEvent): void {
    if (!this.mode) return;
    if (e.button === 0) {
      this.commit();
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.mode) return;
    if (e.key === 'Escape') {
      this.cancel();
    } else if (e.key === 'Enter') {
      this.commit();
    }
  }
}
